import React, { useEffect, useState, useCallback } from 'react';
import { Activity, Plus, Trash2, Pencil, X, History } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { developpementApi } from '../../utils/api';

// Référentiel de jalons par domaine (indicatif, 10 sem. – 3 ans)
const DOMAINES = [
  { key: 'Motricité globale', jalons: [
    ['Tient sa tête', '2-4 mois'], ['Se retourne', '4-6 mois'], ['Tient assis sans appui', '6-9 mois'],
    ['Se déplace à 4 pattes', '8-10 mois'], ['Se met debout avec appui', '9-12 mois'],
    ['Marche seul', '12-18 mois'], ['Court', '18-24 mois'], ['Monte les escaliers', '24-36 mois'],
  ] },
  { key: 'Motricité fine', jalons: [
    ['Attrape un objet', '3-5 mois'], ["Passe un objet d'une main à l'autre", '6-8 mois'],
    ['Pince pouce-index', '9-12 mois'], ['Empile des cubes', '15-18 mois'],
    ['Gribouille', '18-24 mois'], ["Tourne les pages d'un livre", '18-24 mois'],
  ] },
  { key: 'Langage & communication', jalons: [
    ['Sourit en réponse', '1-3 mois'], ['Babille', '4-6 mois'], ['Répond à son prénom', '6-9 mois'],
    ['Premiers mots', '10-14 mois'], ['Associe deux mots', '18-24 mois'], ['Fait des petites phrases', '24-36 mois'],
  ] },
  { key: 'Social & affectif', jalons: [
    ['Cherche le contact visuel', '1-3 mois'], ['Rit aux éclats', '4-6 mois'], ['Joue à coucou', '8-10 mois'],
    ['Angoisse de séparation', '8-12 mois'], ['Imite les autres', '12-18 mois'],
    ['Joue à côté des autres', '18-24 mois'], ['Commence à partager', '30-36 mois'],
  ] },
  { key: 'Autonomie', jalons: [
    ['Mange seul avec les doigts', '9-12 mois'], ['Boit au verre', '12-18 mois'],
    ['Mange à la cuillère', '15-18 mois'], ["Aide à s'habiller", '18-24 mois'], ['Propreté amorcée', '24-36 mois'],
  ] },
];

const STATUTS = [
  { v: 'emergent', label: 'Émergent', chip: 'bg-amber-100 text-amber-700 border-amber-200' },
  { v: 'en_cours', label: 'En cours', chip: 'bg-sky-100 text-sky-700 border-sky-200' },
  { v: 'acquis', label: 'Acquis', chip: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
];
const statutInfo = (v) => STATUTS.find(s => s.v === v);

const fmt = (d) => { try { return format(parseISO(d), 'd MMM yyyy', { locale: fr }); } catch { return d || '—'; } };
const today = () => new Date().toISOString().slice(0, 10);

export default function SuiviDeveloppement({ enfantId, enfantPrenom }) {
  const [obs, setObs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { domaine: DOMAINES[0].key, jalon: '', statut: 'en_cours', date_observation: today(), observation: '', auteur: '' };
  const [form, setForm] = useState(blank);

  const load = useCallback(() => {
    setLoading(true);
    developpementApi.getByEnfant(enfantId)
      .then(r => setObs(r.data || []))
      .catch(() => setObs([]))
      .finally(() => setLoading(false));
  }, [enfantId]);
  useEffect(() => { load(); }, [load]);

  // Dernière observation par jalon → statut courant
  const latestByJalon = {};
  obs.forEach(o => { const k = `${o.domaine}||${o.jalon}`; if (!latestByJalon[k]) latestByJalon[k] = o; });

  const openNew = (domaine, jalon) => {
    setEditing(null);
    setForm({ ...blank, domaine: domaine || DOMAINES[0].key, jalon: jalon || '' });
    setShowForm(true);
  };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ domaine: o.domaine || '', jalon: o.jalon || '', statut: o.statut, date_observation: o.date_observation || today(), observation: o.observation || '', auteur: o.auteur || '' });
    setShowForm(true);
  };
  const save = async (e) => {
    e.preventDefault();
    if (!form.jalon.trim()) return;
    const payload = { ...form, enfant_id: enfantId };
    if (editing) await developpementApi.update(editing.id, payload);
    else await developpementApi.create(payload);
    setShowForm(false); setForm(blank); setEditing(null); load();
  };
  const remove = async (id) => { await developpementApi.delete(id); load(); };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const jalonsDuDomaine = (DOMAINES.find(d => d.key === form.domaine) || {}).jalons || [];

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity size={17} className="text-gold-600" />
          <h3 className="font-semibold text-gray-700">Suivi du développement</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowHistory(h => !h)} className="btn-secondary text-xs">
            <History size={14} /> Historique ({obs.length})
          </button>
          <button onClick={() => openNew()} className="btn-primary text-xs">
            <Plus size={14} /> Observer
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-gray-400 text-sm">Chargement…</div>
      ) : (
        <div className="space-y-5">
          {DOMAINES.map(dom => (
            <div key={dom.key}>
              <div className="text-[12px] uppercase tracking-wide text-navy-500 font-semibold mb-2">{dom.key}</div>
              <div className="space-y-1.5">
                {dom.jalons.map(([jalon, age]) => {
                  const o = latestByJalon[`${dom.key}||${jalon}`];
                  const st = o ? statutInfo(o.statut) : null;
                  return (
                    <button key={jalon} onClick={() => o ? openEdit(o) : openNew(dom.key, jalon)}
                      className="w-full flex items-center gap-2 text-left px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
                      <span className="flex-1 text-[13.5px] text-gray-800">{jalon}</span>
                      <span className="text-[11px] text-gray-400 hidden sm:inline">{age}</span>
                      {st ? (
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.chip}`}>{st.label}</span>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 rounded-full border border-dashed border-gray-200 text-gray-300">à observer</span>
                      )}
                      {o?.date_observation && <span className="text-[11px] text-gray-400 w-16 text-right hidden sm:inline">{fmt(o.date_observation)}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Historique complet */}
      {showHistory && (
        <div className="mt-5 pt-4 border-t border-gray-100">
          <div className="text-[12px] uppercase tracking-wide text-navy-500 font-semibold mb-2">Historique des observations</div>
          {obs.length === 0 ? (
            <p className="text-sm text-gray-400">Aucune observation pour le moment.</p>
          ) : (
            <div className="space-y-1.5">
              {obs.map(o => {
                const st = statutInfo(o.statut);
                return (
                  <div key={o.id} className="flex items-start gap-2 text-[13px] px-2.5 py-2 rounded-lg bg-gray-50">
                    <span className="text-gray-400 w-20 flex-shrink-0">{fmt(o.date_observation)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-800">{o.jalon}</span>
                        {st && <span className={`text-[10.5px] px-1.5 py-0.5 rounded-full border ${st.chip}`}>{st.label}</span>}
                        <span className="text-[11px] text-gray-400">{o.domaine}</span>
                      </div>
                      {o.observation && <div className="text-[12.5px] text-gray-600 mt-0.5">{o.observation}</div>}
                      {o.auteur && <div className="text-[11px] text-gray-400 mt-0.5">— {o.auteur}</div>}
                    </div>
                    <button onClick={() => openEdit(o)} className="p-1 text-gray-300 hover:text-navy-600"><Pencil size={13} /></button>
                    <button onClick={() => remove(o.id)} className="p-1 text-gray-300 hover:text-red-500"><Trash2 size={13} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Formulaire d'observation (modal) */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/40" onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-serif text-lg text-navy-900">{editing ? 'Modifier' : 'Nouvelle'} observation{enfantPrenom ? ` — ${enfantPrenom}` : ''}</h4>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>

            <div>
              <label className="text-[12px] text-gray-500">Domaine</label>
              <select value={form.domaine} onChange={e => { set('domaine', e.target.value); }} className="form-input rounded-xl mt-1">
                {DOMAINES.map(d => <option key={d.key} value={d.key}>{d.key}</option>)}
              </select>
            </div>

            <div>
              <label className="text-[12px] text-gray-500">Jalon</label>
              <input list="jalons-list" value={form.jalon} onChange={e => set('jalon', e.target.value)} className="form-input rounded-xl mt-1" placeholder="Choisir ou saisir un jalon…" />
              <datalist id="jalons-list">
                {jalonsDuDomaine.map(([j]) => <option key={j} value={j} />)}
              </datalist>
            </div>

            <div>
              <label className="text-[12px] text-gray-500">Statut</label>
              <div className="flex gap-1.5 mt-1">
                {STATUTS.map(s => (
                  <button type="button" key={s.v} onClick={() => set('statut', s.v)}
                    className={`px-3 py-1.5 rounded-full text-[12.5px] border transition-colors ${form.statut === s.v ? s.chip + ' font-semibold' : 'bg-white text-gray-500 border-gray-200'}`}>{s.label}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] text-gray-500">Date</label>
                <input type="date" value={form.date_observation} onChange={e => set('date_observation', e.target.value)} className="form-input rounded-xl mt-1" />
              </div>
              <div>
                <label className="text-[12px] text-gray-500">Observé par</label>
                <input value={form.auteur} onChange={e => set('auteur', e.target.value)} className="form-input rounded-xl mt-1" placeholder="Prénom" />
              </div>
            </div>

            <div>
              <label className="text-[12px] text-gray-500">Note (optionnel)</label>
              <textarea value={form.observation} onChange={e => set('observation', e.target.value)} rows={2} className="form-input rounded-xl mt-1" placeholder="Contexte, précision…" />
            </div>

            <div className="flex items-center justify-between gap-2 pt-1">
              {editing ? (
                <button type="button" onClick={() => { remove(editing.id); setShowForm(false); }} className="text-[13px] text-red-500 hover:text-red-700 flex items-center gap-1"><Trash2 size={14} /> Supprimer</button>
              ) : <span />}
              <button type="submit" className="btn-primary">{editing ? 'Enregistrer' : 'Ajouter'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
